import React, { useEffect } from "react";
import { useLocation, useQueryParamBoolean, Link } from "polyrouter";
import {
  AcceptInviteMutation,
  useAcceptInviteMutation,
  useAvailableProductSoftwareByPidQuery
} from "generated/graphql";
import { UseMutationState } from "urql/dist/types";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { useNotification, DISMISS } from "collections/Notifications";
import { hasBackgroundImage } from "utilities/misc";
import { THEME_DEFAULT, THEME_LIGHT } from "store/userMeta";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store";
import { getOperatingSystem } from "utilities/osDetection";
import { useTranslation } from "utilities/i18n";
import queryString from "query-string";
import { LoadingIndicator } from "elements";
import { LENS_DESKTOP_WINDOWS, LENS_DESKTOP_MAC } from "Constants";
import { setToggle } from "store/desktop";
import logo from "assets/images/poly_logo_lens.svg";

export type AcceptInvite = "accept-invite";

const noAuthContext = {
  allowUnAuthenticated: true
};

const DownloadDesktop: React.FC = () => {
  const { hash } = useLocation();
  const { t } = useTranslation();
  let [acceptingInvite] = useQueryParamBoolean<AcceptInvite>("accept-invite");
  const [inviteResult, acceptInvite] = useAcceptInviteMutation();

  const { isAuthenticated, isLoading } = useAuth0();

  const loadingAcceptInvite = (
    <LoadingIndicator
      title={t("ACCEPTING_INVITATION")}
      className="theme-independent"
    />
  );

  if (inviteResult.fetching) {
    return loadingAcceptInvite;
  }

  if (acceptingInvite && !inviteResult.data && !inviteResult.error) {
    if (!isAuthenticated && !isLoading) {
      return <DownloadWithAuth />;
    } else {
      if (isLoading) {
        return (
          <LoadingIndicator
            className="theme-independent"
            title={t("LOADING_AUTHENTICATION")}
          />
        );
      } else {
        const { code } = queryString.parse(hash);
        if (code && code !== "") {
          acceptInvite(
            { code: code as string },
            {
              notify: {
                handler: () => {}
              }
            }
          );
        }
      }
    }
  }

  return <DownloadDesktopContent inviteResult={inviteResult} />;
};

const DownloadWithAuth = withAuthenticationRequired(DownloadDesktop, {
  returnTo: () => {
    const { pathname, search, hash } = window.location;
    return [pathname, search, hash].join("");
  }
});

const DownloadDesktopContent: React.FC<{
  inviteResult: UseMutationState<AcceptInviteMutation>;
}> = ({ inviteResult }) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const theme = useSelector((state: State) => {
    return state.userMeta?.theme ?? THEME_DEFAULT;
  });
  const backgroundStyle = hasBackgroundImage(pathname, theme.toLowerCase());
  const dispatch = useDispatch();
  const [addNotification] = useNotification();

  useEffect(() => {
    addInviteResultMessage(inviteResult, t, addNotification);
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteResult]);

  const os = getOperatingSystem() === "MacOS" ? "macOS" : "windows";
  const builds = {
    macOS: {
      notes: t("MAC_REQUIREMENT"),
      pid: LENS_DESKTOP_MAC,
      archiveUrl: ""
    },
    windows: {
      notes: t("WINDOWS_REQUIREMENT"),
      pid: LENS_DESKTOP_WINDOWS,
      archiveUrl: ""
    }
  };

  const setOverrideDesktopRoute = () => {
    dispatch(setToggle({ key: "overrideDesktopRoute", value: true }));
  };

  const { data } = useAvailableProductSoftwareByPidQuery({
    variables: {
      pidWindows: builds.windows.pid,
      pidMac: builds.macOS.pid
    },
    context: noAuthContext
  });

  builds.windows.archiveUrl = data?.windows?.productBuild?.archiveUrl || "";
  builds.macOS.archiveUrl = data?.mac?.productBuild?.archiveUrl || "";

  return (
    <div
      className={`main ${theme === THEME_LIGHT ? "theme-light" : ""}`}
      style={backgroundStyle}
    >
      <div className="fixed-center card card-xl grid download-content">
        <div className="col-6 m-auto">
          <img src="/img/lens-abstracted.png" alt="" />
        </div>
        <div className="col-6 ph-25 pv-2">
          <div className="brand">
            <img
              className="logo logo-lg mv-1"
              src={logo}
              alt="Poly | Lens"
              data-testid="logo"
            />
          </div>
          <h4 className="mv-1">{t("DESKTOP_APP")}</h4>
          <p className="mb-0">{t("DESKTOP_DESCRIPTION")}</p>
          <a href={builds[os].archiveUrl}>
            <button className="btn btn-primary mv-15" data-testid="download">
              {t("DOWNLOAD")}
            </button>
          </a>
          <p className="mb-0 font-too-small">
            {t("OPERATING_SYSTEM_DETECTED")}
          </p>
          <p className="font-too-small">{builds[os].notes}</p>
          <p className="mb-0 font-too-small">{t("DESKTOP_DIFFERENT_OS")}</p>
          <a
            className="link d-inline-block font-too-small"
            href={builds.macOS.archiveUrl}
          >
            {t("LENS_FOR_MAC")}
          </a>
          <a
            className="link d-inline-block ml-15 font-too-small"
            href={builds.windows.archiveUrl}
          >
            {t("LENS_FOR_WINDOWS")}
          </a>
          <p className="redirect-link font-too-small mt-1 mb-0">
            {t("LOOKING_FOR_LENS")}
            <Link className="link" to="/home" onClick={setOverrideDesktopRoute}>
              {t("HERE")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const addInviteResultMessage = (
  result: UseMutationState<AcceptInviteMutation>,
  t: any,
  addNotification: any
) => {
  const { data } = result;

  if (data && data.acceptUserInvite.invite) {
    addNotification({
      id: "accept-success",
      type: "success",
      message: t("INVITATION_ACCEPTED"),
      dismiss: DISMISS.FAST
    });
  } else if (data && !data.acceptUserInvite.invite) {
    addNotification({
      id: "accept-error",
      type: "error",
      message: t("INVITE_ERROR"),
      dismiss: DISMISS.SLOW
    });
  }
  return;
};

export default DownloadDesktop;
